import React, { useCallback, useEffect, useState } from 'react';
import { useAppContext } from '../../providers/AppContext';
import {
  useNotificationAnchor,
  useNotificationCenter
} from '../../providers/NotificationCenter';
import { NotificationAnchorId } from '../../providers/NotificationCenter/types';
import { Tab, TabList, Tabs } from '../tabs';
import {
  CapillaryPressure,
  WaterOilRelativePermeability,
  GasOilRelativePermeability,
  XAxisToggle
} from './components';

export type CorrelationGroup = 'capillary' | 'water-oil' | 'gas-oil';

export const CORRELATION_GROUPS: { key: CorrelationGroup; label: string }[] = [
  { key: 'capillary', label: 'Capillary Pressure' },
  { key: 'water-oil', label: 'Kr Water-Oil' },
  { key: 'gas-oil', label: 'Kr Gas-Oil' }
];

/**
 * Maps a notification anchor to the group that owns it, so a deep-link to a
 * group that is not currently active can switch to it before scrolling. The
 * `correlation-root` anchor lives on the always-mounted wrapper, so it is not
 * mapped here (no group switch needed).
 */
const ANCHOR_TO_GROUP: Partial<Record<NotificationAnchorId, CorrelationGroup>> = {
  'correlation-capillary-pressure': 'capillary',
  'correlation-water-oil': 'water-oil',
  'correlation-gas-oil': 'gas-oil'
};

/**
 * Segmented (pill) selector for the three correlation groups. Exported so the
 * page can render it in its sticky toolbar while the `Correlation` body renders
 * only the active group (controlled via `activeGroup`/`onActiveGroupChange`).
 */
export const CorrelationGroupSelector = ({
  value,
  onChange
}: {
  value: CorrelationGroup;
  onChange: (group: CorrelationGroup) => void;
}) => (
  <Tabs
    value={value}
    onChange={next => onChange(next as CorrelationGroup)}
    variant="pill"
    size="md"
  >
    <TabList>
      {CORRELATION_GROUPS.map(group => (
        <Tab key={group.key} value={group.key}>
          {group.label}
        </Tab>
      ))}
    </TabList>
  </Tabs>
);

type CorrelationProps = {
  actions?: React.ReactNode;
  hideHeader?: boolean;
  /** Controlled active group. When omitted the component owns the state. */
  activeGroup?: CorrelationGroup;
  onActiveGroupChange?: (group: CorrelationGroup) => void;
  /** Render the built-in pill selector. Pass false when the page renders its own. */
  showGroupSelector?: boolean;
};

const Correlation = ({
  actions,
  hideHeader = false,
  activeGroup: controlledGroup,
  onActiveGroupChange,
  showGroupSelector = true
}: CorrelationProps) => {
  const { configStates } = useAppContext();
  const { registerCorrelationGroupResolver } = useNotificationCenter();
  const correlationRootAnchor = useNotificationAnchor('correlation-root');
  const capillaryAnchor = useNotificationAnchor('correlation-capillary-pressure');
  const waterOilAnchor = useNotificationAnchor('correlation-water-oil');
  const gasOilAnchor = useNotificationAnchor('correlation-gas-oil');

  const [internalGroup, setInternalGroup] = useState<CorrelationGroup>('capillary');
  const activeGroup = controlledGroup ?? internalGroup;

  const setGroup = useCallback(
    (group: CorrelationGroup) => {
      onActiveGroupChange?.(group);
      if (controlledGroup === undefined) {
        setInternalGroup(group);
      }
    },
    [controlledGroup, onActiveGroupChange]
  );

  // Only the active group is mounted, so a notification that targets a
  // non-active group must switch first; the anchor then registers and the
  // pending scroll (retried by the notification center) resolves.
  useEffect(() => {
    registerCorrelationGroupResolver(anchorId => {
      const group = ANCHOR_TO_GROUP[anchorId];
      if (group) {
        setGroup(group);
      }
    });
    return () => registerCorrelationGroupResolver(null);
  }, [registerCorrelationGroupResolver, setGroup]);

  return configStates.loadingProjectSamples ? (
    <div
      style={{
        marginTop: '5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}
    >
      <img src="loading.svg" alt="Loading" />
    </div>
  ) : (
    <div
      ref={correlationRootAnchor}
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        gap: '1.5rem',
      }}
    >
      {!hideHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <XAxisToggle />
          {actions && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {actions}
            </div>
          )}
        </div>
      )}

      {showGroupSelector && (
        <CorrelationGroupSelector value={activeGroup} onChange={setGroup} />
      )}

      {activeGroup === 'capillary' && (
        <div ref={capillaryAnchor} tabIndex={-1}>
          <CapillaryPressure />
        </div>
      )}
      {activeGroup === 'water-oil' && (
        <div ref={waterOilAnchor} tabIndex={-1}>
          <WaterOilRelativePermeability />
        </div>
      )}
      {activeGroup === 'gas-oil' && (
        <div ref={gasOilAnchor} tabIndex={-1}>
          <GasOilRelativePermeability />
        </div>
      )}
    </div>
  );
};

export default Correlation;
